import { MiniCalendar } from "./MiniCalendar";
import { SlotStrip } from "./SlotStrip";
import { BunnyGreeter } from "./BunnyGreeter";
import { emitMascotEvent } from "@/features/mascot/mascotBus";
import "./bunny-greeter.css";

interface LandingHeroProps {
  onCreateLink: () => void;
}

function ClockIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6.4" stroke="var(--lp-plum-500)" strokeWidth="1.3"/>
      <path d="M8 4.5V8l2.4 1.4" stroke="var(--lp-plum-500)" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}

export function LandingHero({ onCreateLink }: LandingHeroProps) {
  return (
    /* data-parallax-surface: the region that *listens* for the pointer. The drift, tilt and
       blob still move relative to the preview card, but they now respond anywhere in the
       hero rather than only inside the card's own ~18% of it, which is why the first view
       felt inert until the cursor happened to cross the calendar. */
    <section className="lp-hero" data-parallax-surface>
      <div className="lp-container" style={{ position: "relative" }}>
        <div className="lp-hero-grid" style={{ position: "relative", zIndex: 1 }}>

          {/* ── Copy column ── */}
          <div className="lp-hero-copy">
            <span className="lp-eyebrow" data-reveal data-reveal-hold style={{ color: "var(--lp-plum-400)" }}>
              BunnyCal · scheduling, gently
            </span>

            <h1 className="lp-display" data-reveal data-reveal-hold style={{ marginTop: 24, marginBottom: 0 }}>
              Your calendar,<br />
              <em>simplified.</em>
            </h1>

            {/* Split into two sentences so the phone can keep the concrete half
                and drop the atmospheric half — three lines of copy before the
                button is the main thing that made the mobile hero read as busy.
                Desktop renders both spans, i.e. the original sentence. */}
            <p className="lp-lede" data-reveal data-reveal-hold style={{ marginTop: 30, maxWidth: "44ch" }}>
              One link. Held slots. Confirmed in seconds.{" "}
              <span className="lp-lede-tail">
                The quiet calendar layer for hosts who care how a meeting begins.
              </span>
            </p>

            {/* Primary CTA — the real handler. */}
            <div data-reveal data-reveal-hold style={{ marginTop: 34 }}>
              <button
                type="button"
                className="lp-btn lp-btn-primary lp-btn-lg"
                /* Pointer-down, not click: navigation unmounts the hero almost immediately, so
                   a reaction fired on click is never actually seen. The press beat plays during
                   the gap before the route changes and never delays it. */
                onPointerDown={() => emitMascotEvent("cta-press")}
                onClick={onCreateLink}
              >
                Create your BunnyCal
                <span style={{ fontSize: 18, lineHeight: 0 }}>→</span>
              </button>
            </div>

            {/* Honest, no-metrics reassurance. */}
            <div className="lp-hero-trust" data-reveal data-reveal-hold style={{ marginTop: 22 }}>
              <div>
                <strong style={{ color: "var(--lp-plum-700)", fontWeight: 580 }}>Free to start</strong>
                <span className="lp-divider-dot" />
                no credit card
                {/* The rest of the reassurance is desktop-only: on a phone this
                    line sits directly under a full-width button and only needs
                    to answer "will this cost me anything". */}
                <span className="lp-trust-tail">, set up your first link in minutes</span>
              </div>
            </div>

          </div>

          {/* Lifted out of `.lp-hero-copy` so it is a direct grid child: that lets the
              mobile layout order it *after* the calendar preview. It is decorative,
              and on a phone it was sitting between the CTA and the product proof,
              pushing the thing that actually sells the page below the fold. */}
          <div className="lp-hero-greeter-slot">
            <BunnyGreeter />
            {/* Mobile-only. On a phone the mascot ends up alone in a ~230px band
                between the booking card and the next section, left-aligned in a
                full-width row with nothing to anchor it — it reads as a leftover
                asset rather than a placed one. This line gives it something to
                react to: it closes the loop on the card directly above, which
                ends on "Confirmed · Tue 10:00 am". Desktop doesn't show it —
                there the mascot sits in the copy column and is already anchored
                by the headline beside it. */}
            <p className="lp-greeter-note">
              That’s the whole flow. Your guest picked the time -
              <strong>no emails, no back-and-forth.</strong>
            </p>
          </div>

          {/* ── Preview column — parallax host: the layers move relative to this card. The
                pointer is read from the hero-wide [data-parallax-surface] above, so the
                motion answers to the cursor anywhere in the first view. ── */}
          <div className="lp-preview-stack" data-parallax-host aria-hidden="true">
            {/* Cursor-follow accent blob — behind everything, eases toward the pointer. */}
            <span
              data-blob
              className="lp-anim-pulse"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: 420,
                height: 420,
                margin: "-210px 0 0 -210px",
                borderRadius: "50%",
                pointerEvents: "none",
                zIndex: 0,
                filter: "blur(60px)",
                background:
                  "radial-gradient(50% 50% at 50% 50%, var(--lp-lilac-soft) 0%, transparent 70%)",
              }}
            />
            <div className="lp-preview-halo" />

            {/* Brand mark: off the card corner */}
            <div className="lp-preview-bunny" data-depth="0.06">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <path d="M8 11.5c0-3.5 1.2-7 1.8-7.2.6-.2 1 .8 1.2 2 .2 1.2.2 2.7.2 4.2" stroke="#2B1F3D" strokeWidth="1.3" strokeLinecap="round"/>
                <path d="M16 11.5c0-3.5-1.2-7-1.8-7.2-.6-.2-1 .8-1.2 2-.2 1.2-.2 2.7-.2 4.2" stroke="#2B1F3D" strokeWidth="1.3" strokeLinecap="round"/>
                <path d="M5.5 16.5c0-3.2 2.9-5.5 6.5-5.5s6.5 2.3 6.5 5.5c0 2.6-2.4 3.5-6.5 3.5s-6.5-.9-6.5-3.5z" stroke="#2B1F3D" strokeWidth="1.3"/>
                <circle cx="10" cy="16.4" r=".7" fill="#2B1F3D"/>
                <circle cx="14" cy="16.4" r=".7" fill="#2B1F3D"/>
              </svg>
            </div>

            {/* Main booking card — the primary parallax layer */}
            <div className="lp-preview-card lp-preview-main" data-reveal data-reveal-hold data-depth="0.025">
              <div style={{ padding: "22px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  {/* plum-400 rather than the plum-300 used elsewhere: at 10px
                      this line only reached 3.0:1 against the card, below the
                      4.5:1 WCAG AA minimum. plum-400 is the next step on the
                      same ramp (5.8:1) so the tone is unchanged, just readable.
                      Scoped here on purpose — the plum-300 token has 16 other
                      usages at larger sizes that already pass. */}
                  <div style={{
                    fontFamily: "var(--lp-mono)", fontSize: 10,
                    letterSpacing: ".18em", textTransform: "uppercase",
                    color: "var(--lp-plum-400)",
                  }}>
                    bunnycal.io / samantha
                  </div>
                  <div className="lp-h3" style={{ marginTop: 5 }}>Intro chat · 30 min</div>
                </div>
                <span className="lp-chip">
                  <span className="lp-dot" />
                  Available
                </span>
              </div>
              <MiniCalendar />
              <SlotStrip />

              {/* Mobile-only status row. The two floating chips below bleed
                  outside the card, which only works while the preview column has
                  room around it — at 390px they landed on top of the card's own
                  header and date grid. They are hidden there and their meaning
                  reappears here, docked inside the card. Display-only, matching
                  the chips they replace (the whole preview is aria-hidden). */}
              <div className="lp-preview-status">
                <span className="lp-chip" style={{ background: "var(--lp-sage-soft)", borderColor: "var(--lp-sage)" }}>
                  <span className="lp-dot" style={{ background: "var(--lp-sage)" }} />
                  Confirmed · Tue 10:00 am
                </span>
                <span className="lp-preview-status-hold">
                  <ClockIcon />
                  9:32 left
                </span>
              </div>
            </div>

            {/* Floating chips — deeper parallax so they lead the drift */}
            <div className="lp-preview-float lp-preview-confirmed" data-depth="0.05">
              <span className="lp-chip" style={{ background: "var(--lp-sage-soft)", borderColor: "var(--lp-sage)" }}>
                <span className="lp-dot" style={{ background: "var(--lp-sage)" }} />
                Confirmed · Tue 10:00 am
              </span>
            </div>

            <div className="lp-preview-float lp-preview-hold" data-depth="0.08">
              <span className="lp-chip">
                <ClockIcon />
                Holding · 9:32 left
              </span>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
